import { MenuOutlined } from '@ant-design/icons';
import { Select } from '@threatadvisor/react-components';
import { TopLevelMenu } from '@threatadvisor/react-components/src/Menu';
import { windowWidth } from '@threatadvisor/react-utils';
import { Space, Typography } from 'antd';
import { useClientContext } from 'ClientContext';
import { ReactElement } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { locations, TARoute } from 'SiteMap';

export type CMMenuProps = {
  extras?: ReactElement;
  isLoading?: boolean;
  accountUrl?: string;
};

export default function CMMenu(props: CMMenuProps): ReactElement {
  const { isLoading, accountUrl } = props;
  const browserWidth = windowWidth() as unknown as number;

  const createMenuItem = (location: TARoute, depth?: number, parentRoute?: string) => {
    depth = (depth ?? 0) + 1;
    const rChildren = (location.items ?? []).filter(c => c.title);
    if (rChildren.length > 0 && location.key !== 'reports') {
      return {
        ...(depth > 1 ? { type: 'vertical' } : {}),
        key: location.key,
        label: (
          <Space>
            <>{location.icon}</>
            <>{location.title}</>
          </Space>
        ),
        children: rChildren.map(c =>
          createMenuItem(c, depth, (parentRoute ? `${parentRoute}/` : '') + location.route)
        )
      };
    } else {
      return location.route === 'divider'
        ? {
            type: 'divider' // Must have
          }
        : {
            key: location.key,
            label: (
              <Link to={(parentRoute ? `${parentRoute}/` : '') + location.route}>
                {location.title}
              </Link>
            ),
            icon: location.icon
          };
    }
  };

  const regularMenuItems = locations.filter(l => l.title).map(l => createMenuItem(l));
  const reducedWindowMenuItems = [
    {
      key: 'collapsedMenuItems',
      label: 'Menu',
      icon: <MenuOutlined />,
      children: regularMenuItems
    }
  ];

  return (
    <TopLevelMenu
      title="ThreatAdvisor MDR"
      children={browserWidth < 958 ? reducedWindowMenuItems : regularMenuItems}
      baseUrl={import.meta.env.VITE_OIDC_AUTHORITY}
      isLoading={isLoading}
      accountUrl={accountUrl ? accountUrl : undefined}
      extrasBeforeAccount={<ClientSelect />}
    ></TopLevelMenu>
  );
}

export function ClientSelect(): ReactElement {
  const { clientId } = useParams();
  const { clients, clientsLoading } = useClientContext();
  const navigate = useNavigate();

  const selectedClientName = () => {
    if (!clients || clients.length === 0) return null;
    const foundClient = clients.find(c => c.id === parseInt(clientId ?? '0'));
    return foundClient ? foundClient.name : null;
  };

  return !clientsLoading && clients && clients?.length > 1 ? (
    <Select
      disabled={clientsLoading}
      loading={clientsLoading}
      popupMatchSelectWidth
      value={clientId}
      style={{ width: 200 }}
      onChange={str => navigate(`/${str.toString()}/cases/detections`)}
      // options={
      //   clients
      //     .sort((a, b) => (a.name as string).localeCompare(b.name as string))
      //     .map(client => ({ label: client.name, value: client.id }))
      // }
    >
      {clients
        ?.sort((a, b) => (a.name as string).localeCompare(b.name as string))
        .map(client => (
          <Select.Option key={client.id} value={client.id?.toString()}>
            {client.name}
          </Select.Option>
        ))}
    </Select>
  ) : (
    <Typography.Text>{selectedClientName()}</Typography.Text>
  );
}
